import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  Col,
  message,
  Row,
  Spin,
  Table,
} from 'antd';
import { Button, COLORS } from 'components';
import { useSelector } from 'react-redux';
import { selectUser } from 'store/auth';
import { useNavigate } from 'react-router';
import {
  getEstatusDeObligacionesFiscales,
  getObligacionesFiscales,
  getObligacionesFiscalesDeContribuyentes,
} from 'api/catalogos';
import { gettersListMap, getTipoDePadronPorContenType } from 'api/padrones';
import { toMoment } from 'utils/normalizers';
import { selectPadrones } from 'store/catalogos';
import { padronesEnum } from 'views/Tramites/GPM/enums';
import {
  ciudadanoToString,
  contribuyenteToString,
  empresaToString,
  infraccionesToString,
  licenciaFuncionamientoToString,
  padronesDerivadosEmpresaDefaultToString,
  predioToString,
  remolqueToString,
  vehiculoToString,
} from 'utils/padronesToString';
import { PROD } from 'utils/env';
import { DollarCircleOutlined } from '@ant-design/icons';
import { getClasificadoresDeTiposDeCargo } from 'api/recaudacion';
import toSlug from 'utils/toSlug';

const extractData = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if ('results' in data) return data.results;
  return [];
};

const customPadronToString = ({
  padrones,
  _padron,
  tipoDePadron,
  options,
}) => {
  const { withTipoDePadron = false, withAddress = null } = options;
  if (!_padron) return '';
  if (typeof _padron !== 'object') return PROD ? '' : 'Padron is not an object ';
  const padron = { ..._padron };
  const padronToStringMap = {
    [padronesEnum.CIUDADANO]: ciudadanoToString,
    [padronesEnum.EMPRESA]: empresaToString,
    [padronesEnum.PREDIO]: (predio) => predioToString(predio, withAddress),
    [padronesEnum.VEHICULO]: vehiculoToString,
    [padronesEnum.CONTRIBUYENTE]: contribuyenteToString,
    [padronesEnum.MOTOCICLETA]: remolqueToString,
    [padronesEnum.REMOLQUE]: remolqueToString,
    [padronesEnum.INFRACCIONES]: infraccionesToString,
    [padronesEnum.MULTAS]: infraccionesToString,
    [padronesEnum.EXPEDIENTE_LICENCIA_FUNCIONAMIENTO]: licenciaFuncionamientoToString,
  };

  const tipoDePadronStr = withTipoDePadron ? padrones.find((p) => p.id === tipoDePadron)?.descripcion.toUpperCase().concat(':') : '';
  const padronString = padronToStringMap[tipoDePadron]?.(padron) || padronesDerivadosEmpresaDefaultToString(padron)?.trim() || '';
  return `${tipoDePadronStr} ${padronString || 'Sin información'}`;
};

const MisPagos = () => {
  const padrones = useSelector(selectPadrones);
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const [loading, setLoading] = useState(true);
  const [objectsPadrones, setObjectsPadrones] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [data, setData] = useState([]);

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      const [
        _obligacionesFiscales,
        _estadoDeObligacionesFiscales,
        _data,
      ] = await Promise.all([
        getObligacionesFiscales(),
        getEstatusDeObligacionesFiscales(),
        getObligacionesFiscalesDeContribuyentes({ contribuyente: user.id }),
      ]);
      if (mounted) {
        const mappedData = _data.map((d) => {
          const obligacion_fiscal = d.obligacion_fiscal
            ? _obligacionesFiscales.find((o) => o.id === d.obligacion_fiscal) : null;
          const tipoDePadron = getTipoDePadronPorContenType(d.content_type_padron);
          const content_type_padron = padrones.find((p) => p.id === tipoDePadron);
          const fecha_inicio = toMoment(d.fecha_inicio).format('DD/MM/YYYY');
          const fecha_ultimo_estado = toMoment(d.fecha_ultimo_estado).format('DD/MM/YYYY');
          const estatus_de_obligaciones_fiscal = _estadoDeObligacionesFiscales
            .find((e) => e.id === d.estatus_de_obligaciones_fiscal)?.descripcion;
          const promise = gettersListMap[tipoDePadron]({ id: d.object_id_padron });

          return {
            ...d,
            obligacion_fiscal,
            _content_type_padron: content_type_padron,
            content_type_padron: content_type_padron?.descripcion || 'Sin información',
            fecha_inicio,
            fecha_ultimo_estado,
            estatus_de_obligaciones_fiscal,
            tipoDePadron,
            promise,
          };
        });
        const _padrones = await Promise.all(mappedData.map((d) => d.promise));
        const _objPadrones = _padrones.map(extractData)
          .flat().map((p) => {
            const label = customPadronToString({
              padrones,
              _padron: p,
              tipoDePadron: p.tipo_de_padron,
              options: { withAddress: p.tipo_de_padron === padronesEnum.PREDIO },
            });

            return {
              ...p,
              key: `${p.id}-${p.tipo_de_padron}`,
              label,
              clave: p.clave,
            };
          });
        setObjectsPadrones(_objPadrones);
        setData(mappedData);
        setLoading(false);
      }
    };
    fetchData();
    return () => { mounted = false; };
  }, []);

  const columns = [
    {
      title: 'Tipo de obligación',
      dataIndex: 'obligacion_fiscal',
      key: 'obligacion_fiscal',
      width: 750,
      responsive: ['xs', 'sm'],
      render: (val) => val?.descripcion || 'Sin información',
    },
    {
      title: 'Padrón',
      dataIndex: 'tipoDePadron',
      key: 'tipoDePadron',
      width: 800,
      responsive: ['xs', 'sm'],
      render: (val, record) => {
        const key = `${record.object_id_padron}-${val}`;
        return objectsPadrones.find((p) => p.key === key)?.label;
      },
    },
    {
      title: 'Fecha de inicio',
      dataIndex: 'fecha_inicio',
      key: 'fecha_inicio',
      width: 320,
      responsive: ['md'],
    },
    {
      title: 'Fecha de fin',
      dataIndex: 'fecha_ultimo_estado',
      key: 'fecha_ultimo_estado',
      width: 320,
      responsive: ['md'],
    },
    {
      title: 'Estado',
      dataIndex: 'estatus_de_obligaciones_fiscal',
      key: 'estatus_de_obligaciones_fiscal',
      width: 320,
      responsive: ['md'],
    },
  ];

  const handleDeclararPago = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = data.find((d) => d.id === key);
    const { obligacion_fiscal } = match;
    const _clasificadores = await getClasificadoresDeTiposDeCargo();
    const subClasificador = _clasificadores.subClasificadores
      .find((c) => c.clase_de_declaracion_empresarial
      === obligacion_fiscal.clase_de_declaracion_empresarial);

    if (!subClasificador) {
      setLoading(false);
      message.warn(`No se encontró una sección para declarar pagos de este padrón. ${window.location.hostname
        .includes('pruebas') || window.location.hostname.includes('localhost')
        ? 'Por favor revise la configuración del clasificador de tipos de cargos.' : ''}`);
      return;
    }
    const _subClasificadores = subClasificador.clasificador.split('.');
    const _clasificador = _clasificadores.clasificadores
      .find((c) => c.clasificador === _subClasificadores[0]);
    const route = `/pagos/${_clasificador
      .nombre} ${_subClasificadores[0]}/${subClasificador
      .nombre} ${_subClasificadores[1]}/pago-compuesto`;

    const [_padron] = await gettersListMap[match.tipoDePadron]({ id: match.object_id_padron });
    const rfc = _padron?.empresa?.contribuyente?.rfc || _padron?.contribuyente?.rfc;
    const slug = toSlug(route);
    const search = new URLSearchParams();
    search.append('tipo_de_padron', match.tipoDePadron);
    search.append('clave', match.clave_cuenta_de_padron_obligado);
    search.append('rfc', rfc);

    navigate(`${slug}?${search.toString()}`);
  };

  return (
    <Spin spinning={loading} tip="Cargando...">
      <Container>
        <Row justify="end">
          <Button
            onClick={handleDeclararPago}
            $accent={selectedRowKeys.length !== 0}
            icon={<DollarCircleOutlined />}
            disabled={selectedRowKeys.length === 0}
          >
            Declarar pago
          </Button>
        </Row>
        <br />
        <Row>
          <Col span={24}>
            <Table
              columns={columns}
              dataSource={data}
              loading={loading}
              rowSelection={{
                selectedRowKeys,
                type: 'radio',
              }}
              onRow={(record) => ({
                onClick: () => {
                  setSelectedRowKeys([record.id]);
                },
              })}
              rowKey="id"
              size="small"
            />
          </Col>
        </Row>
      </Container>
    </Spin>
  );
};

const Container = styled.div`
.ant-table-thead > tr > th {
  background-color: ${COLORS.accent};
  color: white;
  font-weight: bold;
}
.ant-form-item {
  margin-bottom: 5px;
}
`;

export default MisPagos;
